import { kovala, type Aralik, type CubukBoyu } from './cubukBoyu';
import { sayi, type GunlukKarSatiri, type KanalSatiri, type Sayisal } from './ozetHesaplari';
import { kanalSuz, subeSuz } from './subeSuzgeci';
import { kanalSirala } from './kanalRenk';

/** Grafiklerdeki gun etiketi: GG.AA.YYYY. */
const tarihEt = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

const HAFTA_GUNLERI = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

export interface HaftaGunu {
  ad: string;
  ortalama: number;
  gunSayisi: number;
}

/**
 * Haftanin gunlerine gore ORTALAMA ciro. Toplam degil: Agustos'ta bes pazartesi, dort sali olan ay pazartesiyi
 * sisirirdi. Satisi olmayan gun paydaya girmez (veri gelmeyen gun 0 sayilmaz, ZamanBlok kuraliyla ayni).
 */
export function haftaGunleri(gunluk: ReadonlyArray<{ tarih: string; ciro: number }>): HaftaGunu[] {
  const kova = HAFTA_GUNLERI.map(() => ({ toplam: 0, adet: 0 }));
  for (const g of gunluk) {
    const i = (new Date(`${g.tarih}T00:00:00Z`).getUTCDay() + 6) % 7;
    kova[i].toplam += g.ciro;
    kova[i].adet += 1;
  }
  return HAFTA_GUNLERI.map((ad, i) => ({
    ad,
    ortalama: kova[i].adet > 0 ? kova[i].toplam / kova[i].adet : 0,
    gunSayisi: kova[i].adet,
  })).filter((g) => g.gunSayisi > 0);
}

export interface KanalSeyri {
  gruplar: string[];
  noktalar: Array<Record<string, number | string>>;
}

/** Kanal grubu bazinda ciro seyri: ust uste cubuk icin. Filtreler 990040 anahtarini bozmamak icin istemcide. */
export function kanalSeyri(
  rows: KanalSatiri[], sube: string | null, kanal: string | null, boy: CubukBoyu, aralik: Aralik,
): KanalSeyri {
  const gunGrup = new Map<string, Map<string, number>>();
  for (const r of kanalSuz(subeSuz(rows, sube), kanal)) {
    const g = String(r.kanal_grup);
    const m = gunGrup.get(g) ?? new Map<string, number>();
    const t = String(r.tarih).slice(0, 10);
    m.set(t, (m.get(t) ?? 0) + sayi(r.net_ciro));
    gunGrup.set(g, m);
  }
  const gruplar = kanalSirala(Array.from(gunGrup.keys()));
  const noktalar = new Map<string, Record<string, number | string>>();
  const ekle = (etiket: string, grup: string, ciro: number) => {
    const n = noktalar.get(etiket) ?? { etiket };
    n[grup] = ciro;
    noktalar.set(etiket, n);
  };
  const tumGunler = Array.from(new Set(Array.from(gunGrup.values()).flatMap((m) => Array.from(m.keys())))).sort();
  if (boy === 'gun') {
    for (const t of tumGunler) for (const g of gruplar) ekle(tarihEt(t), g, gunGrup.get(g)?.get(t) ?? 0);
  } else {
    // Sira tum satirlardan kurulan kovalardan: bir grubun bos kovasi noktayi kaydirmasin.
    for (const k of kovala(tumGunler.map((tarih) => ({ tarih, ciro: 0 })), aralik, boy)) noktalar.set(k.etiket, { etiket: k.etiket });
    for (const g of gruplar) {
      const seri = Array.from(gunGrup.get(g)!.entries()).map(([tarih, ciro]) => ({ tarih, ciro }));
      for (const k of kovala(seri, aralik, boy)) ekle(k.etiket, g, k.ciro);
    }
  }
  return { gruplar, noktalar: Array.from(noktalar.values()) };
}

export interface MarjNoktasi {
  etiket: string;
  marj: number | null;
}

/** Donem seyri: kar / urun cirosu (ekranin TEK marj tanimi, F-2). Kovada oranlar degil tutarlar toplanir. */
export function marjSeyri(rows: GunlukKarSatiri[], boy: CubukBoyu, aralik: Aralik): MarjNoktasi[] {
  const gunluk = rows
    .map((r) => ({ tarih: String(r.gun).slice(0, 10), ciro: sayi(r.urun_ciro), kar: sayi(r.kar) }))
    .sort((a, b) => a.tarih.localeCompare(b.tarih));
  const marj = (kar: number, ciro: number) => (ciro > 0 ? (kar / ciro) * 100 : null);
  if (boy === 'gun') return gunluk.map((g) => ({ etiket: tarihEt(g.tarih), marj: marj(g.kar, g.ciro) }));
  const karKova = kovala(gunluk.map((g) => ({ tarih: g.tarih, ciro: g.kar })), aralik, boy);
  return kovala(gunluk, aralik, boy).map((k, i) => ({ etiket: k.etiket, marj: marj(karKova[i].ciro, k.ciro) }));
}

export interface UrunKarSatiri {
  stok_kodu: string;
  urun_adi: string;
  kategori?: string;
  ciro: Sayisal;
  maliyet: Sayisal;
  kar: Sayisal;
}

export interface UrunKar {
  stokKodu: string;
  urunAdi: string;
  ciro: number;
  kar: number;
  marj: number;
}

/**
 * En cok kar birakan ve en dusuk marjli urunler. Dusuk marj listesi yalniz cirosu ortalamanin ustundeki urunlerden:
 * birkac liralik urun listeyi doldurmasin. Zararina satilanlar ayri blokta, burada marji sifirin altinda olan girmez.
 */
export function urunKarSiralari(rows: UrunKarSatiri[], n = 10): { enKarli: UrunKar[]; dusukMarj: UrunKar[] } {
  const urunler = rows
    .filter((r) => sayi(r.ciro) > 0)
    .map((r) => ({
      stokKodu: String(r.stok_kodu),
      urunAdi: String(r.urun_adi),
      ciro: sayi(r.ciro),
      kar: sayi(r.kar),
      marj: (sayi(r.kar) / sayi(r.ciro)) * 100,
    }));
  const ortalama = urunler.reduce((t, u) => t + u.ciro, 0) / Math.max(urunler.length, 1);
  return {
    enKarli: [...urunler].sort((a, b) => b.kar - a.kar).slice(0, n),
    dusukMarj: urunler.filter((u) => u.ciro >= ortalama && u.marj >= 0).sort((a, b) => a.marj - b.marj).slice(0, n),
  };
}

export interface SubeKategoriMatrisi {
  subeler: string[];
  kategoriler: Array<{ ad: string; hucreler: Record<string, number>; toplam: number }>;
  enBuyuk: number;
}

/** 990017 -> kategori × sube. Sube ve kategori ciroya gore azalan; cirosuz sube sutun olmaz. */
export function subeKategoriMatrisi(
  rows: Array<{ magaza: string; kategori: string; ciro: Sayisal }>, sube: string | null,
): SubeKategoriMatrisi {
  const subeToplam = new Map<string, number>();
  const kat = new Map<string, Record<string, number>>();
  for (const r of rows) {
    const s = String(r.magaza);
    if (sube !== null && s !== sube) continue;
    const c = sayi(r.ciro);
    subeToplam.set(s, (subeToplam.get(s) ?? 0) + c);
    const h = kat.get(String(r.kategori)) ?? {};
    h[s] = (h[s] ?? 0) + c;
    kat.set(String(r.kategori), h);
  }
  const subeler = Array.from(subeToplam.entries()).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]).map(([s]) => s);
  const kategoriler = Array.from(kat.entries())
    .map(([ad, hucreler]) => ({ ad, hucreler, toplam: subeler.reduce((t, s) => t + (hucreler[s] ?? 0), 0) }))
    .sort((a, b) => b.toplam - a.toplam);
  const enBuyuk = Math.max(0, ...kategoriler.flatMap((k) => subeler.map((s) => k.hucreler[s] ?? 0)));
  return { subeler, kategoriler, enBuyuk };
}
